import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { 
  ArrowLeft, CheckCircle, AlertCircle, DollarSign,
  CreditCard, Wallet, Receipt, Package, Calculator
} from 'lucide-react'
import { api, currencyDenominationsApi } from '../../lib/api'
import toast from 'react-hot-toast'

interface OrderItem {
  id: string
  quantity: number
  unit_price: number
  product: {
    id: string
    name: string
    image_url?: string
  }
  variant?: {
    variant_name: string
  }
}

interface Order {
  id: string
  store_id: string
  customer_name: string
  customer_phone: string
  total_amount: number
  delivery_fee: number
  items: OrderItem[]
}

const DeliveryCompleteOrder = () => {
  const navigate = useNavigate()
  const { orderId } = useParams<{ orderId: string }>()
  const [order, setOrder] = useState<Order | null>(null)
  const [loading, setLoading] = useState(true)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [paymentItems, setPaymentItems] = useState<Record<string, { isPaid: boolean; quantityDelivered: number }>>({})
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'digital_wallet' | 'debt'>('cash')
  const [denominations, setDenominations] = useState<Array<{ id: string; denomination: number; name: string; type: 'bill' | 'coin' }>>([])
  const [amountReceived, setAmountReceived] = useState('')
  const [paymentBreakdown, setPaymentBreakdown] = useState<Record<string, number>>({})

  useEffect(() => {
    if (orderId) {
      loadOrder()
    }
    loadDenominations()
  }, [orderId])

  const loadDenominations = async () => {
    try {
      const response = await currencyDenominationsApi.getDenominations()
      if (response.success) {
        const activeDenoms = response.data.filter((d: any) => d.is_active)
        setDenominations(activeDenoms.sort((a: any, b: any) => b.denomination - a.denomination))
      }
    } catch (error: any) {
      console.error('Error loading denominations:', error)
    }
  }

  const loadOrder = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/orders/${orderId}`)
      if (response.data.success) {
        const orderData = response.data.data
        setOrder({
          id: orderData.id,
          store_id: orderData.store_id || orderData.storeId,
          customer_name: orderData.customer_name || orderData.customerName,
          customer_phone: orderData.customer_phone || orderData.customerPhone,
          total_amount: Number(orderData.total_amount || orderData.totalAmount) || 0,
          delivery_fee: Number(orderData.delivery_fee || orderData.deliveryFee) || 0,
          items: (orderData.items || []).map((item: any) => ({
            id: item.id,
            quantity: Number(item.quantity) || 0,
            unit_price: Number(item.unit_price || item.unitPrice) || 0,
            product: {
              id: item.product?.id || item.product_id,
              name: item.product?.name || 'Producto',
              image_url: item.product?.image_url || item.product?.imageUrl
            },
            variant: item.variant ? {
              variant_name: item.variant.variant_name || item.variant.variantName
            } : undefined
          }))
        })
        
        // Initialize payment items
        const initialPaymentItems: Record<string, { isPaid: boolean; quantityDelivered: number }> = {}
        orderData.items.forEach((item: any) => {
          initialPaymentItems[item.id] = {
            isPaid: item.is_paid !== false,
            quantityDelivered: item.quantity_delivered || item.quantity
          }
        })
        setPaymentItems(initialPaymentItems)
      }
    } catch (error: any) {
      console.error('Error loading order:', error)
      toast.error('Error al cargar el pedido')
      navigate(-1)
    } finally {
      setLoading(false)
    }
  }

  const updateItemPayment = (itemId: string, field: 'isPaid' | 'quantityDelivered', value: boolean | number) => {
    setPaymentItems({
      ...paymentItems,
      [itemId]: {
        ...paymentItems[itemId],
        [field]: value
      }
    })
  }

  const calculateTotals = () => {
    if (!order) return { totalPaid: 0, totalDebt: 0, orderTotal: 0 }
    
    let totalPaid = 0
    let totalDebt = 0
    
    order.items.forEach(item => {
      const payment = paymentItems[item.id] || { isPaid: true, quantityDelivered: item.quantity }
      const quantity = payment.quantityDelivered
      const itemTotal = item.unit_price * quantity
      
      if (payment.isPaid) {
        totalPaid += itemTotal
      } else {
        totalDebt += itemTotal
      }
    })
    
    const orderTotal = totalPaid + (totalDebt > 0 ? 0 : order.delivery_fee)
    
    return { totalPaid, totalDebt, orderTotal }
  }

  const calculateBreakdownTotal = () => {
    let total = 0
    Object.keys(paymentBreakdown).forEach(denomId => {
      const denom = denominations.find(d => d.id === denomId)
      if (denom) {
        total += denom.denomination * (paymentBreakdown[denomId] || 0)
      }
    })
    return total
  }

  const calculateChange = () => {
    const { orderTotal } = calculateTotals()
    // Si hay desglose de billetes, usar ese total; si no, usar el monto ingresado
    const breakdownTotal = calculateBreakdownTotal()
    const received = breakdownTotal > 0 ? breakdownTotal : (parseFloat(amountReceived) || 0)
    return Math.max(0, received - orderTotal)
  }

  const updateBreakdown = (denominationId: string, quantity: number) => {
    setPaymentBreakdown({
      ...paymentBreakdown,
      [denominationId]: Math.max(0, quantity)
    })
    
    // Actualizar amountReceived basado en el breakdown
    const newTotal = calculateBreakdownTotal()
    setAmountReceived(newTotal.toFixed(2))
  }

  const updateAmountReceived = (value: string) => {
    setAmountReceived(value)
    // Si el usuario ingresa un monto manualmente, limpiar el breakdown
    // para que pueda ingresar el desglose después
    if (value && parseFloat(value) > 0) {
      const breakdownTotal = calculateBreakdownTotal()
      // Solo limpiar si el breakdown no coincide con el monto ingresado
      if (Math.abs(breakdownTotal - parseFloat(value)) > 0.01) {
        setPaymentBreakdown({})
      }
    }
  }

  const handleComplete = async () => {
    if (!order) return

    try {
      setUpdatingStatus(true)
      
      const items = order.items.map(item => ({
        itemId: item.id,
        isPaid: paymentItems[item.id]?.isPaid ?? true,
        quantityDelivered: paymentItems[item.id]?.quantityDelivered ?? item.quantity
      }))

      // Validar que si es pago en efectivo y está completamente pagado, tenga monto recibido
      if (paymentMethod === 'cash' && totalDebt === 0) {
        const breakdownTotal = calculateBreakdownTotal()
        const receivedAmount = breakdownTotal > 0 ? breakdownTotal : parseFloat(amountReceived || '0')
        
        if (receivedAmount <= 0) {
          toast.error('Debes ingresar el monto recibido del cliente o el desglose de billetes/monedas')
          setUpdatingStatus(false)
          return
        }

        const { orderTotal } = calculateTotals()
        if (receivedAmount < orderTotal) {
          toast.error(`El monto recibido (RD$${receivedAmount.toFixed(2)}) es menor al total a pagar (RD$${orderTotal.toFixed(2)})`)
          setUpdatingStatus(false)
          return
        }
      }

      const breakdown = paymentMethod === 'cash' && Object.keys(paymentBreakdown).length > 0
        ? Object.keys(paymentBreakdown)
            .filter(denomId => paymentBreakdown[denomId] > 0)
            .map(denomId => ({
              denominationId: denomId,
              quantity: paymentBreakdown[denomId]
            }))
        : undefined

      const response = await api.completeDelivery(order.id, {
        paymentMethod,
        items,
        paymentBreakdown: breakdown
      })
      
      if (response.success) {
        toast.success('Entrega completada exitosamente')
        navigate('/store/delivery')
      } else {
        toast.error(response.message || 'Error al completar entrega')
      }
    } catch (error: any) {
      console.error('Error completing delivery:', error)
      toast.error(error.response?.data?.message || 'Error al completar entrega')
    } finally {
      setUpdatingStatus(false)
    }
  }

  const { totalPaid, totalDebt } = calculateTotals()

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <div className="flex items-center justify-center h-screen">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-100 border-t-blue-600"></div>
            <div className="absolute inset-0 animate-spin rounded-full h-16 w-16 border-4 border-transparent border-r-indigo-600" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
          </div>
        </div>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-gray-400" />
            </div>
            <p className="text-lg font-bold text-gray-900">Pedido no encontrado</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 pb-24">
      {/* Header - Modern Design */}
      <div className="bg-white/70 backdrop-blur-2xl border-b border-white/60 sticky top-0 z-10">
        <div className="max-w-md lg:max-w-7xl mx-auto px-4 lg:px-8 py-5 lg:py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-3 lg:p-4 -ml-2 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 transition-all active:scale-95 shadow-lg shadow-black/5"
            >
              <ArrowLeft className="w-6 h-6 lg:w-7 lg:h-7 text-gray-700" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Completar Entrega
              </h1>
              <p className="text-xs lg:text-sm text-gray-500 font-medium mt-0.5">#{order.id.slice(0, 8).toUpperCase()}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-md lg:max-w-7xl mx-auto px-4 lg:px-8 py-6 lg:py-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-8 space-y-5 lg:space-y-0">
          {/* Left Column */}
          <div className="space-y-5 lg:space-y-6">
            {/* Payment Method - Modern Design */}
            <div className="bg-white/70 backdrop-blur-2xl rounded-[2rem] lg:rounded-3xl shadow-2xl shadow-black/5 border border-white/60 p-6 lg:p-8">
              <h2 className="text-lg lg:text-xl font-bold text-gray-900 mb-5 lg:mb-6">Método de Pago</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
                {[
                  { value: 'cash', label: 'Efectivo', icon: DollarSign, color: 'emerald', gradient: 'from-emerald-500 to-green-600' },
                  { value: 'card', label: 'Tarjeta', icon: CreditCard, color: 'blue', gradient: 'from-blue-500 to-indigo-600' },
                  { value: 'digital_wallet', label: 'Digital', icon: Wallet, color: 'purple', gradient: 'from-purple-500 to-pink-600' },
                  { value: 'debt', label: 'Deuda', icon: Receipt, color: 'red', gradient: 'from-red-500 to-rose-600' }
                ].map((method) => {
                  const Icon = method.icon
                  const isSelected = paymentMethod === method.value
                  return (
                    <button
                      key={method.value}
                      onClick={() => setPaymentMethod(method.value as any)}
                      className={`p-5 lg:p-6 rounded-2xl lg:rounded-3xl border-2 transition-all active:scale-95 shadow-lg ${
                        isSelected
                          ? `border-${method.color}-500 bg-gradient-to-br ${method.gradient} text-white shadow-${method.color}-500/40`
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <Icon className={`w-7 h-7 lg:w-8 lg:h-8 mx-auto mb-2 lg:mb-3 ${
                        isSelected ? 'text-white' : 'text-gray-400'
                      }`} />
                      <span className={`text-sm lg:text-base font-bold ${
                        isSelected ? 'text-white' : 'text-gray-600'
                      }`}>
                        {method.label}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Products Management - Modern Design */}
            <div className="bg-white/70 backdrop-blur-2xl rounded-[2rem] lg:rounded-3xl shadow-2xl shadow-black/5 border border-white/60 p-6 lg:p-8">
              <h2 className="text-lg lg:text-xl font-bold text-gray-900 mb-3 lg:mb-4 flex items-center gap-3">
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl lg:rounded-3xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <Package className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                </div>
                Gestionar Productos
              </h2>
              <p className="text-sm lg:text-base text-gray-600 mb-5 lg:mb-6 font-medium">
                Indica qué productos el cliente recibió y cuáles no. Los productos no recibidos se devolverán al stock.
              </p>
              <div className="space-y-4 lg:space-y-5">
            {order.items.map((item) => {
              const payment = paymentItems[item.id] || { isPaid: true, quantityDelivered: item.quantity }
              const quantityNotDelivered = item.quantity - payment.quantityDelivered
              const isFullyDelivered = payment.quantityDelivered === item.quantity
              const isPartiallyDelivered = payment.quantityDelivered > 0 && payment.quantityDelivered < item.quantity
              const isNotDelivered = payment.quantityDelivered === 0
              
              return (
                <div key={item.id} className={`rounded-2xl p-6 border-2 transition-all shadow-lg ${
                  isFullyDelivered && payment.isPaid
                    ? 'bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-300'
                    : isPartiallyDelivered || isNotDelivered
                    ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-300'
                    : !payment.isPaid
                    ? 'bg-gradient-to-br from-red-50 to-rose-50 border-red-300'
                    : 'bg-gradient-to-br from-gray-50 to-gray-100/50 border-gray-300'
                }`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 flex items-center gap-2 mb-1">
                        {item.product.name}
                        {isFullyDelivered && payment.isPaid && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                        {(isPartiallyDelivered || isNotDelivered) && (
                          <AlertCircle className="w-5 h-5 text-amber-600" />
                        )}
                        {!payment.isPaid && (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      {item.variant && (
                        <div className="text-sm text-gray-600 mb-2">- {item.variant.variant_name}</div>
                      )}
                      <div className="text-sm text-gray-600">
                        Pedido: <span className="font-medium">{item.quantity}</span> unidades | 
                        Precio: <span className="font-medium">RD${item.unit_price.toFixed(2)}</span>
                      </div>
                      {isPartiallyDelivered && (
                        <div className="text-xs text-amber-700 mt-2 bg-amber-100 px-3 py-1.5 rounded-lg inline-block">
                          ⚠ {quantityNotDelivered} unidad(es) no entregada(s) - se devolverá al stock
                        </div>
                      )}
                      {isNotDelivered && (
                        <div className="text-xs text-red-700 mt-2 bg-red-100 px-3 py-1.5 rounded-lg inline-block">
                          ✗ Producto no entregado - se devolverá al stock
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-4 pt-4 border-t border-gray-200">
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-3 block">
                        Cantidad que el cliente recibió (se quedó):
                      </label>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => {
                            if (payment.quantityDelivered > 0) {
                              updateItemPayment(item.id, 'quantityDelivered', payment.quantityDelivered - 1)
                            }
                          }}
                          disabled={payment.quantityDelivered === 0}
                          className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 text-gray-700 font-bold text-2xl disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition-transform shadow-lg border border-gray-300"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min="0"
                          max={item.quantity}
                          value={payment.quantityDelivered}
                          onChange={(e) => {
                            const val = Math.max(0, Math.min(item.quantity, parseInt(e.target.value) || 0))
                            updateItemPayment(item.id, 'quantityDelivered', val)
                          }}
                          className="flex-1 px-4 py-4 border-2 border-gray-300 rounded-2xl text-center font-bold text-2xl focus:border-blue-500 focus:outline-none bg-white shadow-lg"
                        />
                        <button
                          onClick={() => {
                            if (payment.quantityDelivered < item.quantity) {
                              updateItemPayment(item.id, 'quantityDelivered', payment.quantityDelivered + 1)
                            }
                          }}
                          disabled={payment.quantityDelivered >= item.quantity}
                          className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 text-gray-700 font-bold text-2xl disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition-transform shadow-lg border border-gray-300"
                        >
                          +
                        </button>
                      </div>
                      <div className="text-sm text-gray-500 mt-2 text-center">
                        {payment.quantityDelivered} de {item.quantity} unidades
                      </div>
                    </div>
                    
                    {payment.quantityDelivered > 0 && (
                      <div>
                        <label className="text-sm font-semibold text-gray-700 mb-3 block">
                          Estado de pago:
                        </label>
                        <button
                          onClick={() => updateItemPayment(item.id, 'isPaid', !payment.isPaid)}
                          className={`w-full px-5 py-4 rounded-2xl font-bold text-base transition-all active:scale-95 flex items-center justify-center gap-3 shadow-xl ${
                            payment.isPaid
                              ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-emerald-500/40'
                              : 'bg-gradient-to-r from-red-500 to-rose-600 text-white shadow-red-500/40'
                          }`}
                        >
                          {payment.isPaid ? (
                            <>
                              <CheckCircle className="w-5 h-5" />
                              <span>✓ Cliente Pagó</span>
                            </>
                          ) : (
                            <>
                              <AlertCircle className="w-5 h-5" />
                              <span>✗ No Pagó - Agregar a Deuda</span>
                            </>
                          )}
                        </button>
                        {!payment.isPaid && (
                          <div className="text-xs text-red-700 mt-3 bg-red-100 px-4 py-2 rounded-lg">
                            💰 El monto de {payment.quantityDelivered} unidad(es) × RD${item.unit_price.toFixed(2)} = 
                            <span className="font-bold"> RD${(payment.quantityDelivered * item.unit_price).toFixed(2)}</span> se agregará a la deuda del cliente
                          </div>
                        )}
                      </div>
                    )}
                    
                    {payment.quantityDelivered === 0 && (
                      <div className="text-center py-3 bg-gray-100 rounded-xl">
                        <span className="text-sm text-gray-500 italic">
                          Si el cliente no recibió este producto, se devolverá automáticamente al stock
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-5 lg:space-y-6">
            {/* Payment Details - Only show for cash payments */}
            {paymentMethod === 'cash' && totalDebt === 0 && (
              <div className="bg-white/70 backdrop-blur-2xl rounded-[2rem] lg:rounded-3xl shadow-2xl shadow-black/5 border border-white/60 p-6 lg:p-8">
                <h2 className="text-lg lg:text-xl font-bold text-gray-900 mb-5 lg:mb-6 flex items-center gap-2">
                  <Calculator className="w-5 h-5 lg:w-6 lg:h-6" />
                  Pago en Efectivo
                </h2>
                
                {/* Amount Received Input */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Monto Recibido del Cliente (RD$)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amountReceived}
                    onChange={(e) => updateAmountReceived(e.target.value)}
                    className="w-full px-4 py-4 border-2 border-gray-300 rounded-2xl text-2xl font-bold text-center focus:border-blue-500 focus:outline-none"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Ingresa el monto recibido o usa el desglose de billetes/monedas abajo
                  </p>
                </div>

                {/* Breakdown by Denominations */}
                {denominations.length > 0 && (
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Desglose de Billetes/Monedas Recibidos
                    </label>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {denominations.map((denom) => (
                        <div key={denom.id} className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-700 w-32">
                            {denom.name}:
                          </span>
                          <button
                            onClick={() => updateBreakdown(denom.id, (paymentBreakdown[denom.id] || 0) - 1)}
                            disabled={(paymentBreakdown[denom.id] || 0) === 0}
                            className="w-10 h-10 rounded-xl bg-gray-200 text-gray-700 font-bold disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
                          >
                            −
                          </button>
                          <input
                            type="number"
                            min="0"
                            value={paymentBreakdown[denom.id] || 0}
                            onChange={(e) => updateBreakdown(denom.id, parseInt(e.target.value) || 0)}
                            className="w-20 px-3 py-2 border-2 border-gray-300 rounded-xl text-center font-bold focus:border-blue-500 focus:outline-none"
                          />
                          <button
                            onClick={() => updateBreakdown(denom.id, (paymentBreakdown[denom.id] || 0) + 1)}
                            className="w-10 h-10 rounded-xl bg-gray-200 text-gray-700 font-bold active:scale-95"
                          >
                            +
                          </button>
                          <span className="text-sm font-semibold text-gray-600 ml-auto">
                            = RD${((paymentBreakdown[denom.id] || 0) * denom.denomination).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex justify-between text-sm font-semibold">
                        <span className="text-gray-700">Total del Desglose:</span>
                        <span className="text-blue-600">RD${calculateBreakdownTotal().toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Change Calculation */}
                {(amountReceived && parseFloat(amountReceived) > 0) || calculateBreakdownTotal() > 0 ? (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 border-2 border-blue-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-semibold text-gray-700">Total a Pagar:</span>
                      <span className="text-lg font-bold text-gray-900">RD${calculateTotals().orderTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-semibold text-gray-700">Recibido:</span>
                      <span className="text-lg font-bold text-gray-900">
                        RD${(calculateBreakdownTotal() > 0 ? calculateBreakdownTotal() : parseFloat(amountReceived || '0')).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t-2 border-blue-300">
                      <span className="text-base font-bold text-gray-900">Devuelta:</span>
                      <span className={`text-2xl font-bold ${
                        calculateChange() >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        RD${calculateChange().toFixed(2)}
                      </span>
                    </div>
                    {calculateChange() < 0 && (
                      <p className="text-xs text-red-600 mt-2">
                        ⚠️ El monto recibido es menor al total a pagar
                      </p>
                    )}
                    {calculateChange() >= 0 && (
                      <p className="text-xs text-green-600 mt-2 text-center">
                        ✓ Devuelta calculada correctamente
                      </p>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            {/* Totals - Modern Design */}
            <div className="bg-white/70 backdrop-blur-2xl rounded-[2rem] lg:rounded-3xl shadow-2xl shadow-black/5 border border-white/60 p-6 lg:p-8">
              <h2 className="text-lg lg:text-xl font-bold text-gray-900 mb-5 lg:mb-6">Resumen de Pago</h2>
              <div className="space-y-4 lg:space-y-5">
                <div className="flex justify-between text-base lg:text-lg">
                  <span className="text-gray-600 font-medium">Total Pagado:</span>
                  <span className="font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent text-xl lg:text-2xl">
                    RD${totalPaid.toFixed(2)}
                  </span>
                </div>
                {totalDebt > 0 && (
                  <div className="flex justify-between text-base lg:text-lg">
                    <span className="text-gray-600 font-medium">Total Deuda:</span>
                    <span className="font-bold bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent text-xl lg:text-2xl">
                      RD${totalDebt.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-xl lg:text-2xl font-bold border-t-2 border-gray-300 pt-4 lg:pt-5">
                  <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Total Orden:</span>
                  <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    RD${calculateTotals().orderTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Complete Button - Modern Design */}
            <button
              onClick={handleComplete}
              disabled={updatingStatus}
              className="w-full px-6 lg:px-8 py-5 lg:py-6 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-[2rem] lg:rounded-3xl font-bold text-lg lg:text-xl shadow-2xl shadow-emerald-500/40 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition-transform mb-6"
            >
              {updatingStatus ? 'Completando...' : 'Completar Entrega'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeliveryCompleteOrder

